/**
 * Purpose: Point calculation, score tracking, grading, GPA, and honor titles.
 * Owns: Adding points on correct answers, tracking streaks, determining the winner,
 *       letter grade calculation, GPA computation, and honor title assignment.
 * Notes: Points earned per correct answer = the dice roll value for that turn.
 *        Incorrect answers earn 0 points with no penalty. Streaks are tracked
 *        for the AI host reaction system (Phase 2 will use them for commentary).
 *        Grading uses the attendance-based formula: 50% free for playing +
 *        earned portion from correct answers − behavior dock. Hint knockdown
 *        multipliers reduce credit for hint-assisted answers by age group.
 *        Grading scale and GPA map defined in classConfig (CS-21).
 */

import type { GameState, Player, PlayerClassProgress } from '../types/game';
import type { AgeGroup } from '../types/question';
import {
  GRADING_SCALE,
  HONOR_TITLES,
  ATTENDANCE_BASE_PERCENT,
  HINT_KNOCKDOWN_MULTIPLIER,
} from '../config/classConfig';

// _______________________________________________
//              SCORE UPDATES
// _______________________________________________

/**
 * Apply a correct answer to the active player's score and stats.
 * Points earned = the dice value rolled this turn.
 * Note: In the new system, processCorrectAnswer in turnManager handles
 * the full correct-answer flow including seat advancement. This function
 * is kept for backward compatibility with GameContext.
 */
export function applyCorrectAnswer(state: GameState): GameState {
  const dicePoints = state.diceValue ?? 0;
  const playerIndex = state.currentPlayerIndex;

  const updatedPlayers = state.players.map((player, index) => {
    if (index !== playerIndex) return player;

    const newStreak = player.currentStreak + 1;
    return {
      ...player,
      score: player.score + dicePoints,
      questionsCorrect: player.questionsCorrect + 1,
      currentStreak: newStreak,
      bestStreak: Math.max(player.bestStreak, newStreak),
    };
  });

  return {
    ...state,
    players: updatedPlayers,
    currentTurn: {
      ...state.currentTurn,
      wasCorrect: true,
      pointsEarned: dicePoints,
    },
  };
}

/**
 * Apply an incorrect answer — no points, reset streak.
 */
export function applyIncorrectAnswer(state: GameState): GameState {
  const playerIndex = state.currentPlayerIndex;

  const updatedPlayers = state.players.map((player, index) => {
    if (index !== playerIndex) return player;

    return {
      ...player,
      questionsWrong: player.questionsWrong + 1,
      currentStreak: 0,
    };
  });

  return {
    ...state,
    players: updatedPlayers,
    currentTurn: {
      ...state.currentTurn,
      wasCorrect: false,
      pointsEarned: 0,
    },
  };
}

// _______________________________________________
//              GRADING SYSTEM
// _______________________________________________

/**
 * Calculate the final percentage grade for a completed class using the
 * attendance-based formula.
 *
 * Formula:
 *   perQuestionValue = (100 - ATTENDANCE_BASE) / totalQuestions
 *   earnedPortion = sum of each correct question's value:
 *     — full value if answered without help
 *     — value × knockdown multiplier if answered with help
 *   grade = ATTENDANCE_BASE + earnedPortion - behaviorDock
 *   grade is clamped to [0, 100]
 *
 * @param totalQuestions - Total questions answered in this class
 * @param correctAnswers - Questions answered correctly (with or without help)
 * @param questionsWithHelp - Subset of correctAnswers that used a hint
 * @param behaviorDock - Flat percentage docked for behavior (0 if none)
 * @param ageGroup - Player's age group (determines hint knockdown multiplier)
 * @returns Final percentage grade (0–100)
 */
export function calculateClassPercentage(
  totalQuestions: number,
  correctAnswers: number,
  questionsWithHelp: number,
  behaviorDock: number,
  ageGroup: AgeGroup,
): number {
  if (totalQuestions === 0) return ATTENDANCE_BASE_PERCENT;

  const earnedPortion = 100 - ATTENDANCE_BASE_PERCENT;
  const perQuestionValue = earnedPortion / totalQuestions;
  const knockdown = HINT_KNOCKDOWN_MULTIPLIER[ageGroup];

  /* Questions answered correctly without help get full value */
  const unaided = correctAnswers - questionsWithHelp;
  const aidedValue = questionsWithHelp * perQuestionValue * knockdown;
  const unaidedValue = unaided * perQuestionValue;

  const rawGrade = ATTENDANCE_BASE_PERCENT + unaidedValue + aidedValue - behaviorDock;

  /* Clamp to [0, 100] — behavior dock can't push below zero */
  return Math.max(0, Math.min(100, rawGrade));
}

/**
 * Convert a percentage grade to a letter grade using the grading scale.
 * Uses the thresholds from classConfig (no magic numbers, CS-20).
 *
 * @param percentage - Final percentage grade (0–100)
 * @returns Letter grade string (A, B, C, D, or F)
 */
export function percentageToLetterGrade(percentage: number): string {
  for (const threshold of GRADING_SCALE) {
    if (percentage >= threshold.minPercent) {
      return threshold.letter;
    }
  }
  return GRADING_SCALE[GRADING_SCALE.length - 1].letter;
}

/**
 * Calculate the letter grade for a completed class using the attendance formula.
 * This is the primary entry point used by turnManager when a player graduates.
 *
 * @param totalQuestions - Total questions answered in this class
 * @param correctAnswers - Questions answered correctly
 * @param questionsWithHelp - Subset of correctAnswers that used a hint
 * @param behaviorDock - Flat percentage docked for behavior
 * @param ageGroup - Player's age group (determines knockdown multiplier)
 * @returns Letter grade string (A, B, C, D, or F)
 */
export function calculateLetterGrade(
  totalQuestions: number,
  correctAnswers: number,
  questionsWithHelp: number,
  behaviorDock: number,
  ageGroup: AgeGroup,
): string {
  const percentage = calculateClassPercentage(
    totalQuestions,
    correctAnswers,
    questionsWithHelp,
    behaviorDock,
    ageGroup,
  );
  return percentageToLetterGrade(percentage);
}

/**
 * Calculate GPA across all passed class periods.
 * Only classes that have been passed (graduated) are included.
 * GPA is the average of the GPA points for each passed class's letter grade.
 *
 * @param classProgress - Array of PlayerClassProgress for all periods
 * @returns GPA on a 4.0 scale (0.0 if no classes passed)
 */
export function calculateGPA(classProgress: PlayerClassProgress[]): number {
  const passedClasses = classProgress.filter((cp) => cp.passed && cp.letterGrade !== null);

  if (passedClasses.length === 0) return 0;

  const totalGpaPoints = passedClasses.reduce((sum, cp) => {
    const gradeEntry = GRADING_SCALE.find((g) => g.letter === cp.letterGrade);
    return sum + (gradeEntry?.gpaPoints ?? 0);
  }, 0);

  /* Round to 2 decimal places for clean display */
  return Math.round((totalGpaPoints / passedClasses.length) * 100) / 100;
}

/**
 * Calculate final GPA that accounts for incomplete (in-progress) classes.
 * Unlike calculateGPA which only counts passed classes, this function also
 * includes classes that have been started (questionsAnswered > 0) but not
 * yet passed — counting them as 0 GPA points (equivalent to F). This
 * prevents GPA inflation when a player hasn't finished all their classes
 * (e.g., in Race mode where the game ends as soon as one player finishes).
 *
 * @param classProgress - Array of PlayerClassProgress for all periods
 * @returns GPA on a 4.0 scale (0.0 if no classes started)
 */
export function calculateFinalGPA(classProgress: PlayerClassProgress[]): number {
  /* Include passed classes AND any class where the player answered questions */
  const relevantClasses = classProgress.filter(
    (cp) => cp.passed || cp.questionsAnswered > 0,
  );

  if (relevantClasses.length === 0) return 0;

  const totalGpaPoints = relevantClasses.reduce((sum, cp) => {
    if (!cp.passed || cp.letterGrade === null) {
      /* Incomplete class — count as 0 GPA points (F equivalent) */
      return sum;
    }
    const gradeEntry = GRADING_SCALE.find((g) => g.letter === cp.letterGrade);
    return sum + (gradeEntry?.gpaPoints ?? 0);
  }, 0);

  /* Round to 2 decimal places for clean display */
  return Math.round((totalGpaPoints / relevantClasses.length) * 100) / 100;
}

/**
 * Get the honor title for a given GPA.
 * Uses the honor title scale from classConfig (CS-21).
 *
 * @param gpa - The player's cumulative GPA
 * @returns Honor title string (e.g., "Summa Cum Laude", "Summer School")
 */
export function getHonorTitle(gpa: number): string {
  for (const honor of HONOR_TITLES) {
    if (gpa >= honor.minGpa) {
      return honor.title;
    }
  }

  /* Fallback */
  return HONOR_TITLES[HONOR_TITLES.length - 1].title;
}

// _______________________________________________
//              LEADERBOARD HELPERS
// _______________________________________________

/** Get players sorted by score descending (for scoreboard display). */
export function getPlayersByScore(players: Player[]): Player[] {
  return [...players].sort((a, b) => b.score - a.score);
}

/** Get players sorted by finish order for Race mode.
 *  Finished players rank first (winner at top), then non-finished players
 *  ranked by progress (period) and score. */
export function getPlayersByFinishOrder(players: Player[]): Player[] {
  return [...players].sort((a, b) => {
    /* Finished players always rank above non-finished */
    if (a.finished && !b.finished) return -1;
    if (!a.finished && b.finished) return 1;
    /* Among same-status players, further progress ranks higher */
    if (a.currentPeriod !== b.currentPeriod) return b.currentPeriod - a.currentPeriod;
    /* Tiebreaker: higher score */
    return b.score - a.score;
  });
}

/** Get players sorted by GPA descending (for honor roll display). */
export function getPlayersByGPA(players: Player[]): Player[] {
  return [...players].sort((a, b) => {
    if (b.gpa !== a.gpa) return b.gpa - a.gpa;
    return b.score - a.score;
  });
}

/** Determine the current leader. Returns null if tied or no players. */
export function getLeader(players: Player[]): Player | null {
  if (players.length === 0) return null;

  const sorted = getPlayersByScore(players);
  const top = sorted[0];
  const second = sorted[1];

  /* If tied for first, there's no clear leader */
  if (second && top.score === second.score) return null;
  return top;
}

/** Check if a player just took the lead after scoring. */
export function didPlayerTakeLead(
  playerIdWhoScored: string,
  previousPlayers: Player[],
  currentPlayers: Player[],
): boolean {
  const previousLeader = getLeader(previousPlayers);
  const currentLeader = getLeader(currentPlayers);

  return (
    currentLeader !== null &&
    currentLeader.id === playerIdWhoScored &&
    previousLeader?.id !== playerIdWhoScored
  );
}

/** Get the overall winner (highest score when game ends). */
export function getWinner(players: Player[]): Player | null {
  return getLeader(players);
}
